import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Library } from 'lucide-react';
import { PageLayout } from '../../../../components/ui/PageLayout';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { getCountries } from '../../../../api/General/Country';
import { getCompanies } from '../../../../api/General/Company';
import * as CatalogueApi from '../../../../api/Platform/statutoryCatalogue';
import type { StatutoryCatalogueTemplateView, StatutoryLeaveTemplateInput } from '../../../../models/attendance';
import { CatalogueEditor, CatalogueRow, type CountryOption } from './CatalogueRow';

/**
 * The platform's statutory leave catalogue (spec statutory-templates-per-company.md, section 7; owner Q4 b,
 * Q9 a, Q10 a). Operator only, on the route and on every endpoint. The Operator manages the catalogue and never
 * reads a company's copies: rows carry copy COUNTS only. The per-company support action re-copies the rows a
 * company is missing for its countries.
 */

type View = 'active' | 'archived';
const ALL = 'all';

const OperatorStatutoryTemplates: React.FC = () => {
  const { t } = useTranslation(['leaves', 'common']);
  const [searchParams] = useSearchParams();
  const { toast, showToast, dismissToast } = useToast();

  const [rows, setRows] = useState<StatutoryCatalogueTemplateView[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>('active');
  const [countryFilter, setCountryFilter] = useState<string>(ALL);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [supportCompany, setSupportCompany] = useState<string>(() => searchParams.get('companyId') ?? '');
  const [supportResult, setSupportResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [catalogueRes, countriesRes, companiesRes] = await Promise.all([
        CatalogueApi.getStatutoryCatalogue(null, true),
        getCountries().catch(() => null),
        getCompanies().catch(() => null),
      ]);
      if (catalogueRes.errors.length > 0) {
        setLoadError(getApiErrorMessage(catalogueRes, t, t('leaves:statutoryTemplates.loadError')));
        return;
      }
      setRows(catalogueRes.payload ?? []);
      if (countriesRes && countriesRes.errors.length === 0) {
        setCountries((countriesRes.payload ?? [])
          .map(c => ({ value: String(c.id), label: `${c.nicename || c.name} (${c.iso})` }))
          .sort((a, b) => a.label.localeCompare(b.label)));
      }
      if (companiesRes && companiesRes.errors.length === 0) {
        setCompanies((companiesRes.payload ?? [])
          .map(c => ({ value: String(c.id), label: c.name }))
          .sort((a, b) => a.label.localeCompare(b.label)));
      }
    } catch (err) {
      setLoadError(getApiErrorMessage(err, t, t('leaves:statutoryTemplates.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const inCountry = (r: StatutoryCatalogueTemplateView) => countryFilter === ALL || String(r.countryId) === countryFilter;
  const activeCount = rows.filter(r => !r.isArchived && inCountry(r)).length;
  const archivedCount = rows.filter(r => r.isArchived && inCountry(r)).length;

  const groups = useMemo(() => {
    const map = new Map<number, { name: string; iso: string; rows: StatutoryCatalogueTemplateView[] }>();
    for (const r of rows) {
      if ((view === 'archived') !== r.isArchived) continue;
      if (countryFilter !== ALL && String(r.countryId) !== countryFilter) continue;
      const g = map.get(r.countryId) ?? { name: r.countryName || r.countryIso || String(r.countryId), iso: r.countryIso || '', rows: [] };
      g.rows.push(r);
      map.set(r.countryId, g);
    }
    return [...map.entries()].map(([id, g]) => ({ id, ...g })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, view, countryFilter]);

  // The filter lists the countries the catalogue covers; the editor lists every country.
  const coveredCountryOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of rows) if (!seen.has(r.countryId)) seen.set(r.countryId, r.countryIso ? `${r.countryName ?? r.countryIso} (${r.countryIso})` : (r.countryName ?? String(r.countryId)));
    return [
      { value: ALL, label: t('leaves:statutoryTemplates.allCountries') },
      ...[...seen.entries()].map(([id, label]) => ({ value: String(id), label })).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [rows, t]);

  const run = async (key: string, call: () => Promise<{ errors: unknown[] }>, success: string): Promise<boolean> => {
    setBusy(key);
    try {
      const res = await call();
      if (res.errors.length > 0) {
        showToast(getApiErrorMessage(res, t, t('common:Error')), 'error');
        return false;
      }
      showToast(success, 'success');
      // Copy counts change server-side on every write; reload rather than patch them here.
      const fresh = await CatalogueApi.getStatutoryCatalogue(null, true);
      if (fresh.errors.length === 0) setRows(fresh.payload ?? []);
      return true;
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('common:Error')), 'error');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const create = async (input: StatutoryLeaveTemplateInput) => {
    const ok = await run('create', () => CatalogueApi.createStatutoryCatalogueTemplate(input), t('leaves:statutoryTemplates.created'));
    if (ok) setAdding(false);
    return ok;
  };

  const save = (row: StatutoryCatalogueTemplateView) => async (input: StatutoryLeaveTemplateInput) => {
    const ok = await run(`save-${row.id}`, () => CatalogueApi.updateStatutoryCatalogueTemplate(row.id, input), t('leaves:statutoryTemplates.saved'));
    if (ok) setEditingId(null);
    return ok;
  };

  const ensureCopies = async () => {
    if (!supportCompany) return;
    setBusy('ensure');
    setSupportResult(null);
    const companyName = companies.find(c => c.value === supportCompany)?.label ?? `#${supportCompany}`;
    try {
      const res = await CatalogueApi.ensureCompanyStatutoryCopies(Number(supportCompany));
      if (res.errors.length > 0) {
        showToast(getApiErrorMessage(res, t, t('common:Error')), 'error');
        return;
      }
      const added = res.payload ?? 0;
      setSupportResult(added > 0
        ? t('leaves:statutoryTemplates.recopyAdded', { added, company: companyName })
        : t('leaves:statutoryTemplates.recopyNothing', { company: companyName }));
      if (added > 0) {
        const fresh = await CatalogueApi.getStatutoryCatalogue(null, true);
        if (fresh.errors.length === 0) setRows(fresh.payload ?? []);
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('common:Error')), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageLayout
      title={t('leaves:statutoryTemplates.catalogueTitle')}
      description={t('leaves:statutoryTemplates.catalogueDescription')}
      icon={Library}
      accentColor="slate"
      maxWidth="5xl"
      headerActions={!adding ? (
        <Button iconName="Plus" onClick={() => { setAdding(true); setEditingId(null); }} disabled={loading || !!loadError} data-testid="statutory-catalogue-add">
          {t('leaves:statutoryTemplates.addTemplate')}
        </Button>
      ) : undefined}
    >
      <div className="flex flex-col gap-6" data-testid="statutory-catalogue-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:w-72">
            <Select
              id="statutory-catalogue-country-filter"
              label={t('leaves:statutoryTemplates.country')}
              options={coveredCountryOptions}
              value={countryFilter}
              onChange={(v: string) => { setCountryFilter(v || ALL); setEditingId(null); }}
              searchable={coveredCountryOptions.length > 8}
              data-testid="statutory-catalogue-country-filter"
            />
          </div>
          <div role="group" aria-label={t('leaves:statutoryTemplates.viewLabel')} className="inline-flex w-full rounded-md border border-border bg-muted p-0.5 sm:w-auto">
            {(['active', 'archived'] as View[]).map(v => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => { setView(v); setEditingId(null); }}
                className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 sm:flex-none ${
                  view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`statutory-catalogue-view-${v}`}
              >
                {v === 'active' ? t('leaves:statutoryTemplates.viewActive') : t('leaves:statutoryTemplates.viewArchived')}
                <span className="ms-1.5 tabular-nums text-muted-foreground">{v === 'active' ? activeCount : archivedCount}</span>
              </button>
            ))}
          </div>
        </div>

        {adding && (
          <section className="rounded-lg border border-primary/30 bg-card px-4 py-5 sm:px-5" data-testid="statutory-catalogue-new">
            <CatalogueEditor
              idPrefix="statutory-catalogue-new"
              heading={t('leaves:statutoryTemplates.newCatalogueRow')}
              countryOptions={countries}
              defaultCountryId={countryFilter !== ALL ? Number(countryFilter) : null}
              countryLocked={false}
              note={t('leaves:statutoryTemplates.catalogueCreateNote')}
              submitLabel={t('leaves:statutoryTemplates.addTemplate')}
              saving={busy === 'create'}
              onCancel={() => setAdding(false)}
              onSubmit={create}
            />
          </section>
        )}

        {loading ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5" data-testid="statutory-catalogue-loading">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex flex-col gap-2 py-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-full max-w-md" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-5" role="alert">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button variant="outline" size="sm" onClick={load}>{t('common:Retry')}</Button>
          </div>
        ) : groups.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-5 py-10 text-center text-sm text-muted-foreground" data-testid="statutory-catalogue-empty">
            {view === 'archived' ? t('leaves:statutoryTemplates.noArchived') : t('leaves:statutoryTemplates.catalogueEmpty')}
          </p>
        ) : (
          groups.map(g => (
            <section key={g.id} aria-labelledby={`statutory-catalogue-country-${g.id}`} className="flex flex-col gap-2">
              <h2 id={`statutory-catalogue-country-${g.id}`} className="flex items-center gap-2 text-sm font-semibold text-foreground">
                {g.name}
                {g.iso && <span className="text-xs font-normal text-muted-foreground">{g.iso}</span>}
              </h2>
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {g.rows.map(row => (
                  <CatalogueRow
                    key={row.id}
                    row={row}
                    countryOptions={countries}
                    editing={editingId === row.id}
                    busy={busy}
                    onEdit={() => { setEditingId(row.id); setAdding(false); }}
                    onCancelEdit={() => setEditingId(null)}
                    onSave={save(row)}
                    onArchive={() => run(`archive-${row.id}`, () => CatalogueApi.archiveStatutoryCatalogueTemplate(row.id),
                      t('leaves:statutoryTemplates.archivedToast', { name: row.leaveTypeName }))}
                    onRestore={() => { run(`restore-${row.id}`, () => CatalogueApi.restoreStatutoryCatalogueTemplate(row.id),
                      t('leaves:statutoryTemplates.restoredToast', { name: row.leaveTypeName })); }}
                  />
                ))}
              </ul>
            </section>
          ))
        )}

        <section
          aria-labelledby="statutory-recopy-heading"
          className="flex flex-col gap-4 rounded-lg border border-border bg-card px-4 py-5 sm:px-5"
          data-testid="statutory-recopy"
        >
          <div className="flex flex-col gap-1">
            <h2 id="statutory-recopy-heading" className="text-sm font-semibold text-foreground">{t('leaves:statutoryTemplates.recopyTitle')}</h2>
            <p className="max-w-prose text-sm text-muted-foreground">{t('leaves:statutoryTemplates.recopyDescription')}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="w-full sm:w-80">
              <Select
                id="statutory-recopy-company"
                label={t('leaves:statutoryTemplates.company')}
                options={companies}
                value={supportCompany}
                onChange={(v: string | number) => { setSupportCompany(v ? String(v) : ''); setSupportResult(null); }}
                placeholder={t('leaves:statutoryTemplates.selectCompany')}
                searchable
                data-testid="statutory-recopy-company"
              />
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={ensureCopies}
              loading={busy === 'ensure'}
              disabled={!supportCompany || busy != null}
              data-testid="statutory-recopy-submit"
            >
              {t('leaves:statutoryTemplates.recopySubmit')}
            </Button>
          </div>
          {supportResult && (
            <p className="text-sm text-foreground" role="status" data-testid="statutory-recopy-result">{supportResult}</p>
          )}
        </section>
      </div>
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default OperatorStatutoryTemplates;
